import React from 'react';
import { trackEvent } from '../../utils/analytics/analytics';
import { normalizeSupportedLocale } from '../../utils/routing/locale-from-pathname';
import type { IntlShape } from 'react-intl';
import { LANG_FLAGS } from './LanguageFlags';
import { LANG_CIRCLE, LANG_FLAG_HALO, LANG_TEXT_ACTIVE, LANG_TEXT_INACTIVE, LANG_TRACK, NAV_PART } from './nav-live-index/nav-styles';

export interface LanguageSelectorProps {
  intl: IntlShape;
  formatMessage: IntlShape['formatMessage'];
  /** Current locale; if missing or invalid, falls back to intl.locale so the button is never empty */
  locale?: 'en' | 'fi' | 'sv';
  className?: string;
  /** 'compact40' shrinks the track to 40px tall with 30px circles, for the navbar live-index
   * redesign where every row-one control is 40px. Omitted everywhere else, so existing usages
   * keep today's 52px track / 36px circles unchanged. */
  sizeVariant?: 'default' | 'compact40';
  /**
   * TEC navbar redesign Phase 4 item 1: renders each language as a plain `<a href>` instead of a
   * `<button onClick>`, with no `window.location`/`trackEvent` side effects: for the prerendered
   * static twin (ResourceNavbar.tsx), which ships no React hydration and cannot run a click
   * handler. `data-lang-pill` is kept on each link either way so the existing hreflang-upgrade
   * script (ResourceNavbar's LANG_PILL_HREFLANG_UPGRADE_SCRIPT) still finds and rewrites them.
   * Omitted everywhere else, so every hydrated usage keeps today's button/onClick behaviour.
   */
  hrefs?: Record<'en' | 'fi' | 'sv', string>;
}

/**
 * Language selector component for navigation.
 * Shows FI/SV/EN as a row of true circles on a white track; the selected language gets its
 * national flag as a 30%-opacity background tint instead of a plain highlight. Matches the SRP
 * header's own language selector (components/search/DesktopFilterCategories.tsx) so the control
 * looks and behaves the same everywhere it appears. Only visible on xl screens and above.
 *
 * The compact40 track/circle classes come from nav-styles.ts (TEC navbar redesign Phase 4 item 1)
 * so ResourceNavbar's `hrefs` usage renders byte-identical markup to this component's own.
 */
export const LanguageSelector: React.FC<LanguageSelectorProps> = ({
  intl,
  formatMessage,
  locale: localeProp,
  className = '',
  sizeVariant = 'default',
  hrefs,
}) => {
  // Ensure we always have a valid display locale (static SSR may pass undefined; hydration may pass wrong value)
  const locale = normalizeSupportedLocale(localeProp ?? intl?.locale);
  const isCompact = sizeVariant === 'compact40';

  const handleLanguageChange = (newLocale: 'en' | 'fi' | 'sv') => {
    trackEvent({
      name: 'locale_change',
      params: {
        to_locale: newLocale,
        from_locale: locale,
        surface: 'other',
      },
    });
    // Imported on click: url-handling carries the role-hub and region-label tables, which pages
    // that only hydrate the navbar (web-static articles) would otherwise ship up front.
    void import('../../utils/search-router/url-handling').then(({ convertUrlToLanguage }) => {
      window.location.href = convertUrlToLanguage(window.location.pathname, newLocale, intl);
    });
  };

  const languageAriaLabel = formatMessage({
    id: 'navigation.language',
    defaultMessage: 'Language',
    description: 'Button aria-label for language selector in navigation',
  });

  const trackClassName = isCompact
    ? `${LANG_TRACK} ${className}`
    : `hidden xl:flex items-center gap-1 h-[52px] px-1.5 rounded-full border border-jc-border/70 bg-jc-surface/90 ${className}`;
  const circleClassName = isCompact ? LANG_CIRCLE : 'relative w-9 h-9 shrink-0 rounded-full overflow-hidden flex items-center justify-center text-xs font-bold uppercase cursor-pointer transition-colors';

  return (
    <div className={trackClassName} data-jc-nav-part={isCompact ? NAV_PART.langTrack : undefined}>
      {(['fi', 'sv', 'en'] as const).map((lang) => {
        const isActive = locale === lang;
        const Flag = LANG_FLAGS[lang];
        const textClassName = isActive ? LANG_TEXT_ACTIVE : LANG_TEXT_INACTIVE;
        const content = (
          <>
            {isActive && (
              <span className={LANG_FLAG_HALO}>
                <Flag />
              </span>
            )}
            <span className={textClassName}>{lang.toUpperCase()}</span>
          </>
        );
        if (hrefs) {
          return (
            <a
              key={lang}
              href={hrefs[lang]}
              // ResourceNavbar's inline script upgrades these hub fallbacks to the page's own locale
              // alternates (from its hreflang links) before hydration, on purpose.
              suppressHydrationWarning
              data-lang-pill={lang}
              data-vike="false"
              aria-current={isActive ? 'true' : undefined}
              aria-label={languageAriaLabel}
              data-jc-nav-part={isCompact ? NAV_PART.langCircle : undefined}
              className={circleClassName}
            >
              {content}
            </a>
          );
        }
        return (
          <button
            key={lang}
            type="button"
            onClick={() => handleLanguageChange(lang)}
            aria-current={isActive ? 'true' : undefined}
            aria-label={languageAriaLabel}
            data-jc-nav-part={isCompact ? NAV_PART.langCircle : undefined}
            className={circleClassName}
          >
            {content}
          </button>
        );
      })}
    </div>
  );
};
